<template>
  <pdl-label-group qaid="product-label-group">
    <pdl-label v-if="isArchivedProduct" kind="default" qaid="product-label-archive">
      {{ $t('product.archived.flag') }}
    </pdl-label>
    <pdl-label v-if="hasSaleFlag" kind="critical" qaid="product-label-sale">
      {{ $t('product.sale.flag') }}
    </pdl-label>
    <pdl-label v-if="hasCPOLabel" kind="prominent" qaid="product-label-CPO">
      {{ $t('product.CPO.flag') }}
    </pdl-label>
    <pdl-label v-if="hasB2bEarlyIntroFlag" kind="default" qaid="product-label-b2b-early-intro">
      {{ $t('text.buyingZone.earlyIntro') }}
    </pdl-label>
    <pdl-label v-if="hasCloseoutFlag" kind="important" qaid="product-label-closeout">
      {{ $t('text.buyingZone.closeout') }}
    </pdl-label>
    <pdl-label v-if="hasProductCalloutNew" kind="positive" qaid="product-label-callout-new">
      {{ $t('product.new.flag') }}
    </pdl-label>
    <pdl-label
      v-if="hasProductCallout"
      :kind="isNewLowerPriceCallout ? 'positive' : 'default'"
      qaid="product-label-callout"
    >
      {{ product.productCallout }}
    </pdl-label>
    <pdl-label v-if="hasPreOrderLabel" kind="prominent" qaid="product-label-pre-order">
      {{ $t('product.preOrder.flag') }}
    </pdl-label>
  </pdl-label-group>
</template>
<script>
import {useFunctionalityToggle} from '@/composables/functionality-toggle';
import {FunctionalityToggle} from '@/constants/functionality-toggle';
import {PdlLabel, PdlLabelGroup} from '@pedal/pdl-label';
import Variant from '@/utils/variant-helpers.js';
import {mapState} from 'vuex';

const ProductBadgeType = Object.freeze({
  CLOSEOUT_SALE: 'C',
  EARLY_INTRO: 'E',
  NEW_LOWER_PRICE: 'L',
  SALE: 'S',
});

export default {
  components: {
    PdlLabel,
    PdlLabelGroup,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    isB2bPdp: Boolean,
    isPlp: Boolean,
    isArchived: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const {isToggleActive: isNewLowerPriceBadgeAllowed} = useFunctionalityToggle(
      FunctionalityToggle.NEW_LOWER_PRICE_BADGE_TOGGLE
    );
    return {isNewLowerPriceBadgeAllowed};
  },
  computed: {
    ...mapState('productDetails', ['variantCode']),
    isArchivedProduct() {
      return this.isArchived || !!this.product.isArchived;
    },
    hasSaleFlag() {
      return this.isSelectedVariantOnSale || this.isPlpProductOnSale;
    },
    hasB2bEarlyIntroFlag() {
      return !!(this.isB2bPdp && this.product.productBadgeStatus === ProductBadgeType.EARLY_INTRO);
    },
    selectedVariant() {
      return Variant(this.variantCode, this.product, this.isB2bPdp).get();
    },
    isSelectedVariantOnSale() {
      return this.selectedVariant?.priceIndicatorFlag === ProductBadgeType.SALE;
    },
    isSelectedVariantOnCloseout() {
      return this.selectedVariant?.priceIndicatorFlag === ProductBadgeType.CLOSEOUT_SALE;
    },
    isSelectedVariantNewLowerPrice() {
      return this.selectedVariant?.priceIndicatorFlag === ProductBadgeType.NEW_LOWER_PRICE;
    },
    isPlpProductOnCloseoutSale() {
      return this.isPlp && this.product?.priceIndicatorFlag === ProductBadgeType.CLOSEOUT_SALE;
    },
    isPlpProductOnSale() {
      return this.isPlp && this.product?.priceIndicatorFlag === ProductBadgeType.SALE;
    },
    isPlpProductNewLowerPrice() {
      return this.isPlp && this.product?.priceIndicatorFlag === ProductBadgeType.NEW_LOWER_PRICE;
    },
    hasCloseoutFlag() {
      return this.isSelectedVariantOnCloseout || this.isPlpProductOnCloseoutSale;
    },
    hasCallout() {
      return !!(this.product.productCallout && this.product.productCallout.length > 0);
    },
    hasProductCalloutNew() {
      return !!(
        this.hasCallout && this.product.productCallout.toLowerCase() === this.$t('product.new.flag').toLocaleLowerCase()
      );
    },
    isNewLowerPriceCallout() {
      return (
        this.hasCallout &&
        (this.product.productCallout.toLowerCase() === 'new lower price' ||
          this.product.productCallout.toLowerCase() === 'nouveau prix réduit')
      );
    },
    hasProductCallout() {
      return !!(
        this.isNewLowerPriceBadgeAllowed &&
        !this.hasProductCalloutNew &&
        (this.isSelectedVariantNewLowerPrice || this.isPlpProductNewLowerPrice)
      );
    },
    hasCPOLabel() {
      return this.product.isCPO;
    },
    hasPreOrderLabel() {
      return this.product?.preOrderFlag;
    },
  },
};
</script>
